/**
 * Shared busy-events pipeline for both Manual and Self-schedule
 * calendars. Owns:
 *   - `/calendar_events` interviewer busy fetch
 *   - Visible-week filter + edit-mode "this interview" dedupe
 *   - Pastel-colored external events (manual shape — reused by both
 *     modes now that self-schedule renders via FullCalendar directly)
 *   - `BusyEvent[]` adapter for the availability algorithm
 *   - Meeting-room busy fetch + merge
 *
 * Manual-only overlays (available slots, no-slots popover, min-notice,
 * unavailable hours) stay in `CalendarPanel` — they assume a single
 * booked-slot workflow that self-schedule doesn't have.
 */
import { useMemo } from 'react';
import moment from 'moment-timezone';
import {
  useInterviewerCalendarChunksQuery,
  type PrefetchWeeks,
} from '@/api/calendarEvents';
import type { BusyEvent } from '../../availability';
import type { ScheduleSlot } from '../../state/useScheduleSharedState';
import { getPastelSlotColorsForUser } from '../interviewerPastelColors';
import { syncScheduleInterviewerPastelKeys } from '../../state/interviewerPastelMap';
import { useMeetingRoomBusyEvents } from './useMeetingRoomBusyEvents';
import type { ManualCalendarExternalEvent } from './useManualCalendarEvents';

interface UseBusyCalendarEventsOptions {
  interviewerIds: number[];
  sortedInterviewerIds: number[];
  meetingRoomEmails: string[];
  meetingRoomNamesByEmail: Record<string, string>;
  visibleWeekStartIso: string;
  visibleWeekEndIso: string;
  resolvedTz: string;
  isLocalTimezone: boolean;
  atsJobId?: number;
  prefetchWeeks: PrefetchWeeks;
  lookaheadPrefetchWeeks: number;
  isEdit?: boolean;
  bookedSlot?: ScheduleSlot | null;
  interviewName?: string;
  originalInterviewName?: string;
  /**
   * How many weeks past the visible week start to include when
   * shaping busy events for the algorithm. Self-schedule feeds the
   * pipeline multi-week availability, so the busy events it subtracts
   * must span the same window. Manual mode only needs the visible
   * week. Defaults to 1 (visible week only).
   */
  algorithmWindowWeeks?: number;
}

export interface BusyCalendarEventsResult {
  /** Interviewer busy pastel events (manual shape). */
  interviewerExternalEvents: ManualCalendarExternalEvent[];
  /** Meeting-room busy pastel events (manual shape). */
  meetingRoomExternalEvents: ManualCalendarExternalEvent[];
  /** Combined interviewer + room events — consumed by both manual and self-schedule. */
  calendarExternalEvents: ManualCalendarExternalEvent[];
  /** Interviewer busy reshaped for the availability / slot algorithm. */
  interviewerBusyForAlgorithm: BusyEvent[];
  /** Combined interviewer + room busy for the algorithm. */
  busyEventsForAlgorithm: BusyEvent[];
  /** True while the visible-week fetch is still resolving. */
  isBlockingBusyFetch: boolean;
  /** True while the room-busy fetch is still resolving. */
  isRoomBusyBlocking: boolean;
  /** First fetch error (if any). */
  fetchError: Error | null;
  /**
   * Per-user metadata from the interviewer busy endpoint
   * (`calendar_available`, etc.) — surfaced so the form can render
   * "calendar not connected" warnings on chips.
   */
  usersInfoById: Record<string, { calendar_available?: boolean }>;
}

export function useBusyCalendarEvents({
  interviewerIds,
  sortedInterviewerIds,
  meetingRoomEmails,
  meetingRoomNamesByEmail,
  visibleWeekStartIso,
  visibleWeekEndIso,
  resolvedTz,
  isLocalTimezone,
  atsJobId,
  prefetchWeeks,
  lookaheadPrefetchWeeks,
  isEdit,
  bookedSlot,
  interviewName,
  originalInterviewName,
  algorithmWindowWeeks = 1,
}: UseBusyCalendarEventsOptions): BusyCalendarEventsResult {
  const {
    mergedEvents: calendarEvents,
    isBlockingBusyFetch,
    fetchError,
    usersInfoById,
  } = useInterviewerCalendarChunksQuery({
    userIds: interviewerIds,
    anchorWeekStartIso: visibleWeekStartIso,
    timezone: resolvedTz,
    prefetchWeeks,
    lookaheadPrefetchWeeks,
    ...(atsJobId !== undefined && atsJobId !== null && atsJobId > 0
      ? { atsJobId }
      : {}),
    enabled: interviewerIds.length > 0,
    isLocalTimezone,
  });

  // Window filter + edit-mode "this interview" dedupe.
  const calendarEventsForVisibleWeek = useMemo(() => {
    const vs = moment(visibleWeekStartIso).valueOf();
    const ve = moment(visibleWeekEndIso).valueOf();
    const editBookedStart =
      isEdit && bookedSlot?.start ? moment(bookedSlot.start).valueOf() : null;
    const editBookedEnd =
      isEdit && bookedSlot?.end ? moment(bookedSlot.end).valueOf() : null;
    return calendarEvents.filter((evt) => {
      const s = moment(evt.start).valueOf();
      const e = moment(evt.end).valueOf();
      if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
      if (s >= ve || e <= vs) return false;
      if (typeof evt.title !== 'string') return true;
      if (
        editBookedStart !== null &&
        editBookedEnd !== null &&
        s === editBookedStart &&
        e === editBookedEnd &&
        evt.title === interviewName
      ) {
        return false;
      }
      if (
        isEdit &&
        originalInterviewName &&
        evt.title === originalInterviewName
      ) {
        return false;
      }
      return true;
    });
  }, [
    calendarEvents,
    visibleWeekEndIso,
    visibleWeekStartIso,
    isEdit,
    bookedSlot,
    interviewName,
    originalInterviewName,
  ]);

  // Pastel-colored external events per interviewer.
  const interviewerExternalEvents = useMemo<
    ManualCalendarExternalEvent[]
  >(() => {
    syncScheduleInterviewerPastelKeys(sortedInterviewerIds);
    const seen = new Set<string>();
    const rows: ManualCalendarExternalEvent[] = [];
    for (const evt of calendarEventsForVisibleWeek) {
      const uidRaw = evt.user_id;
      const uid =
        typeof uidRaw === 'number' && Number.isFinite(uidRaw)
          ? uidRaw
          : sortedInterviewerIds[0];
      if (
        typeof uid === 'number' &&
        Number.isFinite(uid) &&
        !sortedInterviewerIds.includes(uid)
      ) {
        continue;
      }
      const dedupeKey = `${evt.id ?? ''}|${evt.start}|${evt.end}|${uid ?? ''}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      const colors = getPastelSlotColorsForUser(sortedInterviewerIds, uid);
      rows.push({
        id: evt.id ?? `${evt.start}-${evt.end}-${uid ?? ''}`,
        start: evt.start,
        end: evt.end,
        title: evt.title ?? 'Busy',
        backgroundColor: colors.backgroundColor,
        // Border matches fill — legacy KInterviewScheduler parity.
        borderColor: colors.backgroundColor,
        textColor: colors.textColor,
      });
    }
    return rows;
  }, [calendarEventsForVisibleWeek, sortedInterviewerIds]);

  // Self-schedule feeds the pipeline multi-week availability, so the
  // busy events that subtract from it must span the same window. Defaults
  // to the visible week for manual mode.
  const algorithmWindowEndIso = useMemo(
    () =>
      moment(visibleWeekStartIso)
        .tz(resolvedTz)
        .add(algorithmWindowWeeks * 7, 'days')
        .toISOString(),
    [visibleWeekStartIso, resolvedTz, algorithmWindowWeeks]
  );

  const interviewerBusyForAlgorithm = useMemo<BusyEvent[]>(() => {
    const vs = moment(visibleWeekStartIso).valueOf();
    const ve = moment(algorithmWindowEndIso).valueOf();
    const editBookedStart =
      isEdit && bookedSlot?.start ? moment(bookedSlot.start).valueOf() : null;
    const editBookedEnd =
      isEdit && bookedSlot?.end ? moment(bookedSlot.end).valueOf() : null;
    const rows: BusyEvent[] = [];
    for (const evt of calendarEvents) {
      const s = moment(evt.start).valueOf();
      const e = moment(evt.end).valueOf();
      if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
      if (s >= ve || e <= vs) continue;
      if (
        editBookedStart !== null &&
        editBookedEnd !== null &&
        s === editBookedStart &&
        e === editBookedEnd &&
        evt.title === interviewName
      ) {
        continue;
      }
      if (
        isEdit &&
        originalInterviewName &&
        evt.title === originalInterviewName
      ) {
        continue;
      }
      const uidRaw = evt.user_id;
      const uid =
        typeof uidRaw === 'number' && Number.isFinite(uidRaw)
          ? uidRaw
          : sortedInterviewerIds[0];
      if (uid === undefined) continue;
      rows.push({
        id: String(evt.id ?? `${evt.start}-${evt.end}-${uid}`),
        start: evt.start,
        end: evt.end,
        title: evt.title,
        interviewerId: uid,
      });
    }
    return rows;
  }, [
    calendarEvents,
    visibleWeekStartIso,
    algorithmWindowEndIso,
    sortedInterviewerIds,
    isEdit,
    bookedSlot,
    interviewName,
    originalInterviewName,
  ]);

  const {
    events: meetingRoomBusyEvents,
    externalEvents: meetingRoomExternalEvents,
    isBlockingBusyFetch: isRoomBusyBlocking,
  } = useMeetingRoomBusyEvents({
    roomEmails: meetingRoomEmails,
    roomNamesByEmail: meetingRoomNamesByEmail,
    visibleWeekStartIso,
    visibleWeekEndIso,
    timezone: resolvedTz,
    ...(atsJobId !== undefined ? { atsJobId } : {}),
    prefetchWeeks,
    lookaheadPrefetchWeeks,
    enabled: !isLocalTimezone && meetingRoomEmails.length > 0,
  });

  const busyEventsForAlgorithm = useMemo<BusyEvent[]>(
    () => [...interviewerBusyForAlgorithm, ...meetingRoomBusyEvents],
    [interviewerBusyForAlgorithm, meetingRoomBusyEvents]
  );

  const calendarExternalEvents = useMemo<ManualCalendarExternalEvent[]>(
    () => [...interviewerExternalEvents, ...meetingRoomExternalEvents],
    [interviewerExternalEvents, meetingRoomExternalEvents]
  );

  return {
    interviewerExternalEvents,
    meetingRoomExternalEvents,
    calendarExternalEvents,
    interviewerBusyForAlgorithm,
    busyEventsForAlgorithm,
    isBlockingBusyFetch,
    isRoomBusyBlocking,
    fetchError,
    usersInfoById,
  };
}
